import { useCallback } from 'react';
import { useAppDispatch } from '@/store/hooks';
import { showSnackbar } from '@/store/slices/snackbarSlice';
import { useI18nText } from '@/lib/utils/i18nText';

/**
 * Copy a value to the clipboard and show a success toast that echoes the
 * copied value — matching the Candidates advertiser-link copy behavior.
 *
 * Returns `copy(value, label?)`:
 *  - `value`: the text written to the clipboard (skipped when empty)
 *  - `label`: heading shown above the value (defaults to "Link copied")
 */
export function useCopyLink() {
  const dispatch = useAppDispatch();
  const { t } = useI18nText();

  return useCallback((value: string | undefined | null, label?: string) => {
    if (!value) return;
    void navigator.clipboard.writeText(value)
      .then(() => {
        dispatch(showSnackbar({
          text: `${label ?? t('Link copied', '링크가 복사되었습니다')}\n${value}`,
          kind: 'success',
          duration: 3000,
        }));
      })
      .catch(() => {
        dispatch(showSnackbar({
          text: t('Failed to copy', '복사에 실패했습니다'),
          kind: 'api-error',
          duration: 2500,
        }));
      });
  }, [dispatch, t]);
}
